import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE } from "@/utils/wordGenerator/common.js";

export const createLPTemp3 = async (lpData) => {
    const style = lpData.style || {};

    // Heights
    const HEADER_HEIGHT = 1200;
    const SPACER_HEIGHT = 1980;
    const BODY_HEIGHT = 10000;
    const FOOTER_HEIGHT = 1200;
    const IMAGE_SPACER = 300;
    const PROJECT_IMAGE_HEIGHT = 4850;
    const PROJECT_IMAGE_WIDTH = 5565;
    const LOGO_WIDTH = 3200;
    const headerColor = fixColor(style.colorScheme?.header || "#f0f0f0");
    const bodyColor = fixColor(style.colorScheme?.body || "#f0f0f0");
    const footerColor = fixColor(style.colorScheme?.footer || "#f0f0f0");

    // 1. Header Logo (top right)
    let headerLogoRun = null;
    if (lpData.logo) {
        const logoData = await fetchImageWithDimensions(lpData.logo);
        if (logoData) {
            headerLogoRun = fitImage(logoData, LOGO_WIDTH, HEADER_HEIGHT, "contain");
        }
    }

    // 2. Contact Section Logo (left side)
    let contactLogoRun = null;
    if (lpData.logo) {
        const logoData = await fetchImageWithDimensions(lpData.logo);
        if (logoData) {
            contactLogoRun = fitImage(logoData, 3000, 3000, "contain");
        }
    }

    // 3. Fetch project images (max 2)
    const projectRuns = [];
    for (const project of (lpData.projects || []).slice(0, 2)) {
        if (project && (project.uploadedPhoto || project.photoURL)) {
            const imgData = await fetchImageWithDimensions(project.uploadedPhoto || project.photoURL);
            if (imgData) {
                projectRuns.push(imgData);
            } else {
                projectRuns.push(null);
            }
        } else {
            projectRuns.push(null);
        }
    }

    // Placeholder for missing images
    const getPlaceholder = () => [
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
                new TableRow({
                    height: { value: PROJECT_IMAGE_HEIGHT, rule: HeightRule.EXACT },
                    children: [new TableCell({ shading: { fill: bodyColor }, children: [] })]
                })
            ]
        })
    ];

    // 1. HEADER TABLE
    const headerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: HEADER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: 27, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        verticalAlign: VerticalAlign.CENTER,
                        children: headerLogoRun
                            ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [headerLogoRun] })]
                            : []
                    }),
                    new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),
                    new TableCell({
                        width: { size: 70, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        children: []
                    })
                ]
            })
        ]
    });

    // 2. BODY TABLE (Contact Left | Projects Right)
    const bodyTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    // Margin Left
                    new TableCell({ width: { size: 2, type: WidthType.PERCENTAGE }, children: [] }),

                    // LEFT: Contact Information
                    new TableCell({
                        width: { size: 47, type: WidthType.PERCENTAGE },
                        shading: { fill: bodyColor },
                        verticalAlign: VerticalAlign.CENTER,
                        children: [
                            // Logo
                            contactLogoRun
                                ? new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    spacing: { after: 500 },
                                    children: [contactLogoRun]
                                })
                                : new Paragraph(""),

                            // Name
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 200 },
                                children: [
                                    new TextRun({
                                        text: lpData.name || "N/A",
                                        color: fixColor(style.label?.color),
                                        size: (style.label?.size || 24) * SCALE,
                                        font: style.label?.font,
                                        bold: style.label?.bold,
                                        italics: style.label?.italic,
                                        underline: style.label?.underline,
                                    })
                                ]
                            }),

                            // Address
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 100 },
                                children: [
                                    new TextRun({
                                        text: lpData.address || "N/A",
                                        color: fixColor(style.text?.color),
                                        size: (style.text?.size || 16) * SCALE,
                                        font: style.text?.font,
                                        bold: style.text?.bold,
                                        italics: style.text?.italic,
                                        underline: style.text?.underline,
                                    })
                                ]
                            }),

                            // City, State
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 100 },
                                children: [
                                    new TextRun({
                                        text: `${lpData.city || 'N/A'}, ${lpData.stateProvince || 'N/A'}`,
                                        color: fixColor(style.text?.color),
                                        size: (style.text?.size || 16) * SCALE,
                                        font: style.text?.font,
                                        bold: style.text?.bold,
                                        italics: style.text?.italic,
                                        underline: style.text?.underline,
                                    })
                                ]
                            }),

                            // Contact Number
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 100 },
                                children: [
                                    new TextRun({
                                        text: lpData.contactNo || "N/A",
                                        color: fixColor(style.text?.color),
                                        size: (style.text?.size || 16) * SCALE,
                                        font: style.text?.font,
                                        bold: style.text?.bold,
                                        italics: style.text?.italic,
                                        underline: style.text?.underline,
                                    })
                                ]
                            }),

                            // Email
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({
                                        text: lpData.email || "N/A",
                                        color: fixColor(style.text?.color),
                                        size: (style.text?.size || 16) * SCALE,
                                        font: style.text?.font,
                                        bold: style.text?.bold,
                                        italics: style.text?.italic,
                                        underline: style.text?.underline,
                                    })
                                ]
                            })
                        ]
                    }),

                    new TableCell({ width: { size: 2, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),

                    // RIGHT: Project Images (nested table with 2 rows)
                    new TableCell({
                        width: { size: 47, type: WidthType.PERCENTAGE },
                        children: [
                            new Table({
                                width: { size: 100, type: WidthType.PERCENTAGE },
                                borders: {
                                    top: { style: BorderStyle.NONE },
                                    bottom: { style: BorderStyle.NONE },
                                    left: { style: BorderStyle.NONE },
                                    right: { style: BorderStyle.NONE },
                                    insideHorizontal: { style: BorderStyle.NONE },
                                    insideVertical: { style: BorderStyle.NONE }
                                },
                                rows: [
                                    // Top Project Image
                                    new TableRow({
                                        height: { value: PROJECT_IMAGE_HEIGHT, rule: HeightRule.EXACT },
                                        children: [
                                            new TableCell({
                                                children: projectRuns[0]
                                                    ? [new Paragraph({
                                                        children: [fitImage(projectRuns[0], PROJECT_IMAGE_WIDTH, PROJECT_IMAGE_HEIGHT, "cover")]
                                                    })]
                                                    : getPlaceholder()
                                            })
                                        ]
                                    }),

                                    // Spacer between images
                                    new TableRow({
                                        height: { value: IMAGE_SPACER, rule: HeightRule.EXACT },
                                        children: [new TableCell({ children: [] })]
                                    }),

                                    // Bottom Project Image
                                    new TableRow({
                                        height: { value: PROJECT_IMAGE_HEIGHT, rule: HeightRule.EXACT },
                                        children: [
                                            new TableCell({
                                                children: projectRuns[1]
                                                    ? [new Paragraph({
                                                        children: [fitImage(projectRuns[1], PROJECT_IMAGE_WIDTH, PROJECT_IMAGE_HEIGHT, "cover")]
                                                    })]
                                                    : getPlaceholder()
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),

                    // Margin Right
                    new TableCell({ width: { size: 2, type: WidthType.PERCENTAGE }, children: [] }),
                ]

            })
        ]
    });

    // 3. FOOTER TABLE
    const footerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: FOOTER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        shading: { fill: footerColor },
                        children: []
                    })
                ]
            })
        ]
    });

    // Main container - stack independent tables with paragraph spacers
    return {
        properties: {
            page: {
                margin: { top: 0, right: 0, bottom: 0, left: 0 },
            },
        },
        children: [
            headerTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            bodyTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            footerTable
        ],
    };
};
